import { computed, type Ref } from "vue";
import { EMOJI_PICKER_VISIBLE_REACTION_LIMIT } from "@/src/design_system/button_and_controls/emoji_picker/constants/layout";
import type { DSEmojiPickerReaction } from "@/src/design_system/button_and_controls/emoji_picker/types/props";
import { sanitizeEmojiPickerReactions } from "@/src/design_system/button_and_controls/emoji_picker/utils/reactions";
import { resolveEmojiUnicodes } from "@/src/shared/emoji/utils";

interface UseEmojiPickerReactionsOptions {
  modelValue: Ref<DSEmojiPickerReaction[]>;
}

export const useEmojiPickerReactions = ({ modelValue }: UseEmojiPickerReactionsOptions) => {
  const modelReactions = computed(() => sanitizeEmojiPickerReactions(modelValue.value));
  const validReactions = computed(() => {
    return modelReactions.value.flatMap(reaction => {
      const emoji = resolveEmojiUnicodes([reaction.emoji])[0];
      return emoji ? [{ ...reaction, emoji: emoji.unicode }] : [];
    });
  });
  const ownReaction = computed(() => validReactions.value.find(reaction => reaction.isOwn));
  const displayReactions = computed(() => {
    const resolved = resolveEmojiUnicodes(validReactions.value.map(reaction => reaction.emoji));
    const visible = resolved.slice(0, EMOJI_PICKER_VISIBLE_REACTION_LIMIT);
    const ownEmoji = ownReaction.value ? resolveEmojiUnicodes([ownReaction.value.emoji])[0] : undefined;

    if (ownEmoji && !visible.some(emoji => emoji.unicode === ownEmoji.unicode)) {
      visible[visible.length - 1] = ownEmoji;
    }

    return visible;
  });
  const hasDisplayReactions = computed(() => displayReactions.value.length > 0);
  const hasOwnReaction = computed(() => Boolean(ownReaction.value));
  const reactionCount = computed(() => validReactions.value.length);

  const updateOwnReaction = (unicode: string): void => {
    const currentReaction = ownReaction.value;
    const otherReactions = validReactions.value.filter(reaction => !reaction.isOwn);

    if (currentReaction?.emoji === unicode) {
      modelValue.value = otherReactions;
      return;
    }

    modelValue.value = [...otherReactions, { emoji: unicode, user: currentReaction?.user ?? {}, isOwn: true }];
  };

  return {
    displayReactions,
    hasDisplayReactions,
    hasOwnReaction,
    reactionCount,
    updateOwnReaction,
  };
};
